<template>
  <StepShell
    :title="t('completeV2.steps.shopConfirm.screenTitle')"
    :intro="t('completeV2.steps.shopConfirm.screenIntro')"
    :can-go-back="canGoBack"
    :continue-disabled="v$.$invalid || loading"
    :continue-loading="saving"
    @next="handleNext"
    @back="$emit('back')"
  >
    <div
      v-if="loading"
      class="shop-confirm__loader"
    >
      <i class="far fa-spinner-third fa-spin" />
      {{ t('completeV2.steps.shopConfirm.loading') }}
    </div>
    <div
      v-else
      class="shop-confirm"
    >
      <!-- ====== SHOP ====== -->
      <section class="shop-confirm__section">
        <header class="shop-confirm__section-header">
          <span class="shop-confirm__section-icon">
            <i class="far fa-store" />
          </span>
          <h3 class="shop-confirm__section-title">
            {{ t('completeV2.steps.shopConfirm.sections.shop') }}
          </h3>
        </header>

        <div class="shop-confirm__grid">
          <div class="shop-confirm__field shop-confirm__field--full">
            <label
              for="sc-name"
              class="shop-confirm__label"
            >{{ t('storeParameters.storeInformations.name') }}</label>
            <InputText
              id="sc-name"
              v-model="field.name"
              :class="{ 'p-invalid': v$.name.$dirty && v$.name.$invalid }"
              @blur="v$.name.$touch()"
            />
          </div>
          <div class="shop-confirm__field">
            <label
              for="sc-category"
              class="shop-confirm__label"
            >{{ t('storeParameters.storeInformations.category') }}</label>
            <Dropdown
              id="sc-category"
              v-model="field.shopCategory"
              :options="categories"
              option-label="label"
              option-value="code"
              :placeholder="t('storeParameters.storeInformations.category')"
            >
              <template #value="slot">
                <span v-if="slot.value">
                  {{ t(categories.find((c) => c.code === slot.value)?.label ?? '') }}
                </span>
                <span v-else>{{ slot.placeholder }}</span>
              </template>
              <template #option="slot">
                {{ t(slot.option.label) }}
              </template>
            </Dropdown>
          </div>
          <div class="shop-confirm__field">
            <label
              for="sc-currency"
              class="shop-confirm__label"
            >{{ t('storeParameters.storeInformations.currency') }}</label>
            <Dropdown
              id="sc-currency"
              v-model="field.currency"
              :options="currencies"
              option-label="label"
              option-value="code"
              filter
            />
          </div>
        </div>
      </section>

      <!-- ====== RGPD ====== -->
      <section class="shop-confirm__section">
        <header class="shop-confirm__section-header">
          <span class="shop-confirm__section-icon">
            <i class="far fa-shield-halved" />
          </span>
          <h3 class="shop-confirm__section-title">
            {{ t('completeV2.steps.shopConfirm.sections.rgpd') }}
          </h3>
        </header>

        <div class="shop-confirm__field">
          <label
            for="sc-privacy"
            class="shop-confirm__label"
          >
            {{ t('storeParameters.rgpd.privacyPolicyUrl') }}
            <i
              v-tooltip.top="t('completeV2.steps.shopConfirm.tooltips.privacyPolicyUrl')"
              class="far fa-circle-question shop-confirm__hint"
            />
          </label>
          <LocalizedTextInput
            id="sc-privacy"
            v-model="field.privacyPolicyUrl"
          />
        </div>
        <div class="shop-confirm__field">
          <label
            for="sc-dpo"
            class="shop-confirm__label"
          >
            {{ t('storeParameters.rgpd.dataManagerEmail') }}
            <i
              v-tooltip.top="t('completeV2.steps.shopConfirm.tooltips.dataManagerEmail')"
              class="far fa-circle-question shop-confirm__hint"
            />
          </label>
          <LocalizedTextInput
            id="sc-dpo"
            v-model="field.dataManagerEmail"
          />
        </div>
      </section>

      <!-- ====== LANGUAGES ====== -->
      <section class="shop-confirm__section">
        <header class="shop-confirm__section-header">
          <span class="shop-confirm__section-icon">
            <i class="far fa-globe" />
          </span>
          <h3 class="shop-confirm__section-title">
            {{ t('completeV2.steps.shopConfirm.sections.languages') }}
          </h3>
        </header>

        <div class="shop-confirm__field">
          <label
            for="sc-lang"
            class="shop-confirm__label"
          >{{ t('storeParameters.storeInformations.language') }}</label>
          <Dropdown
            id="sc-lang"
            v-model="field.defaultLang"
            :options="shopLanguages"
            option-label="label"
            option-value="value"
          >
            <template #value="slot">
              <div
                v-if="slot.value"
                class="shop-confirm__lang"
              >
                <img
                  :src="`/images/flags/${slot.value}.png`"
                  alt=""
                  class="shop-confirm__flag"
                >
                <span>{{ t(`languages.codes.${slot.value}`) }}</span>
              </div>
            </template>
            <template #option="slot">
              <div class="shop-confirm__lang">
                <img
                  :src="`/images/flags/${slot.option.value}.png`"
                  alt=""
                  class="shop-confirm__flag"
                >
                <span>{{ t(`languages.codes.${slot.option.value}`) }}</span>
              </div>
            </template>
          </Dropdown>
        </div>
      </section>
    </div>
  </StepShell>
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, reactive, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import Tooltip from 'primevue/tooltip';
import useVuelidate from '@vuelidate/core';
import { required, minLength, maxLength } from '@vuelidate/validators';
import StepShell from '@/components/onboarding-v2/shared/StepShell.vue';
import LocalizedTextInput from '@/components/fields/LocalizedTextInput.vue';
import categories from '@/configs/shop-categories';
import currencies from '@/configs/currencies';
import fetchShopInformation, { updateShop } from '@/composables/shop/Shops';
import updateShopsLang from '@/composables/shop/ShopsLang';
import { fetchShopConfigurations } from '@/composables/shop/MyShopParameters';
import { SaveShopsConfiguration, saveShopParamsOnRedis } from '@/composables/shop/ShopsConfiguration';
import { UserState, Me } from '@/composables/User';
import { showToastError } from '@/helpers';

interface Field {
  name: string;
  shopCategory: any;
  currency: string;
  defaultLang: any;
  privacyPolicyUrl: Record<string, string>;
  dataManagerEmail: Record<string, string>;
}

export default defineComponent({
  name: 'ShopConfirmStep',

  components: {
    StepShell,
    Dropdown,
    InputText,
    LocalizedTextInput,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    canGoBack: { type: Boolean, default: false },
  },

  emits: ['next', 'back'],

  setup(_, { emit }) {
    const { t } = useI18n();

    const field = reactive<Field>({
      name: '',
      shopCategory: null,
      currency: '',
      defaultLang: null,
      privacyPolicyUrl: {},
      dataManagerEmail: {},
    });

    const shopLanguages = ref<Array<{ label: string; value: string }>>([]);
    const loading = ref(true);
    const saving = ref(false);

    const rules = computed(() => ({
      name: { required, minLength: minLength(3), maxLength: maxLength(25) },
      shopCategory: { required },
      currency: { required },
      defaultLang: { required },
    }));

    const v$ = useVuelidate(rules, field);

    onMounted(async () => {
      try {
        await Me(false);
        const shopId = UserState.activeShop?.id ?? 0;
        const info = await fetchShopInformation({ shopId });
        if (info) {
          const defaultLang = info.defaultLang ?? 'fr';
          const langs = (info.langs ?? [])
            .filter((l: any) => !l.default)
            .map((l: any) => ({ label: l.lang, value: l.lang }));
          langs.unshift({ label: defaultLang, value: defaultLang });
          if (!langs.length) {
            langs.push({ label: 'fr', value: 'fr' });
          }
          shopLanguages.value = langs;

          const rgpd = await fetchShopConfigurations({ shopId }, 'shopRequiredDataRgpd');
          const emptyByLang = () => shopLanguages.value.reduce(
            (acc: Record<string, string>, l) => { acc[l.value] = ''; return acc; },
            {},
          );
          const reduceLang = (items: any[] | null | undefined) => {
            const base = emptyByLang();
            if (!items?.length) return base;
            items.forEach((cur: any) => { base[cur.lang] = cur.value; });
            return base;
          };

          field.name = info.name ?? '';
          field.shopCategory = info.idCategoryShop ?? null;
          field.currency = info.currency ?? '';
          field.defaultLang = defaultLang;
          field.privacyPolicyUrl = reduceLang(
            rgpd?.filter((i: any) => i.key === 'shopRequiredDataRgpd_privacyPolicyUrl'),
          );
          field.dataManagerEmail = reduceLang(
            rgpd?.filter((i: any) => i.key === 'shopRequiredDataRgpd_dataManagerEmail'),
          );
        }
      } catch {
        await showToastError('GENERIC_ERROR');
      } finally {
        loading.value = false;
      }
    });

    const handleNext = async () => {
      const ok = await v$.value.$validate();
      if (!ok) return;
      saving.value = true;
      const shopId = UserState.activeShop?.id ?? 0;
      try {
        await updateShop([{
          id_shop: shopId,
          name: field.name,
          id_category_shop: field.shopCategory,
          currency: field.currency,
        }]);
        await updateShopsLang(
          shopLanguages.value.map((item) => ({
            id_shop: shopId,
            lang: item.value,
            default: item.value === field.defaultLang,
          })),
        );
        const configs = Object.keys(field.privacyPolicyUrl).map((k) => ({
          key: 'shopRequiredDataRgpd_privacyPolicyUrl',
          lang: k,
          value: field.privacyPolicyUrl[k],
        })).concat(Object.keys(field.dataManagerEmail).map((k) => ({
          key: 'shopRequiredDataRgpd_dataManagerEmail',
          lang: k,
          value: field.dataManagerEmail[k],
        })));
        await SaveShopsConfiguration({ id_shop: shopId, configs });
        await saveShopParamsOnRedis(shopId);
        emit('next');
      } catch {
        await showToastError('GENERIC_ERROR');
      } finally {
        saving.value = false;
      }
    };

    return {
      t,
      field,
      v$,
      categories,
      currencies,
      shopLanguages,
      loading,
      saving,
      handleNext,
    };
  },
});
</script>

<style lang="scss" scoped>
.shop-confirm {
  display: flex;
  flex-direction: column;
  gap: 1.85rem;

  &__section {
    display: flex;
    flex-direction: column;
    gap: 0.95rem;
  }

  &__section-header {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin-bottom: 0.1rem;
  }

  &__section-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    background: rgba(148, 200, 64, 0.14);
    color: #5d8a24;
    font-size: 0.82rem;
  }

  &__section-title {
    font-size: 0.88rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: 0.01em;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;

    &--full {
      grid-column: 1 / -1;
    }
  }

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.72rem;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__hint {
    color: #94a3b8;
    font-size: 0.8rem;
    cursor: help;
    transition: color 0.15s ease;

    &:hover { color: #5d8a24; }
  }

  &__loader {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    justify-content: center;
    padding: 2.5rem;
    color: #64748b;
  }

  &__lang {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
  }

  &__flag {
    height: 1rem;
    width: auto;
    border-radius: 0.15rem;
    box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.05);
  }
}

:deep(.p-inputtext) {
  height: 2.3rem;
  border-radius: 0.5rem;
  border-color: #d1d5db;
  font-size: 0.9rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover { border-color: #94c840; }
  &:focus,
  &.p-focus {
    border-color: #94c840;
    box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.18);
  }
}

:deep(.p-dropdown) {
  height: 2.3rem;
  border-radius: 0.5rem;
  border-color: #d1d5db;
  align-items: center;

  &:hover { border-color: #94c840; }
  &.p-focus {
    border-color: #94c840;
    box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.18);
  }
}

:deep(.localizedTextInput) {
  align-items: center;

  .p-inputtext {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .language-selector.p-dropdown {
    height: 2.3rem;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: none;
  }
}

@media (max-width: 640px) {
  .shop-confirm__grid {
    grid-template-columns: 1fr;
  }
}
</style>
